import type { ReactNode } from 'react';
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip } from '@/components/ui/Tooltip';

export type DeltaDirection = 'up' | 'down' | 'neutral' | 'new';
export type BadgeTone = 'success' | 'danger' | 'neutral';

export interface StatCardProps {
  icon?: ReactNode;
  label: string;
  value: ReactNode;
  hint?: string;
  /**
   * Badge d'évolution % vs période précédente. Le placement dépend de
   * `deltaPosition` (default `'inline'` à droite du label, pattern Stripe
   * Dashboard compact).
   */
  delta?: { value: string; direction: DeltaDirection };
  /**
   * Placement du badge `delta` :
   *  - `'inline'` (default) : à droite du label, sur la même ligne.
   *    Compact, pattern Stripe Dashboard.
   *  - `'below'` : sur sa propre ligne sous le label. Le sens « vs N-1 » est
   *    implicite (porté par un tooltip au survol du badge). Peut accueillir un
   *    `deltaTrailing` en bout de ligne. Plus aéré, évite le cluttering du label.
   */
  deltaPosition?: 'inline' | 'below';
  /**
   * Badge secondaire générique - utile pour afficher un compteur additionnel
   * (ex: « 12 résiliés » sous une card « Partenaires actifs »). Sans icône
   * directionnelle ni suffixe N-1, contrairement au `delta`. Affiché sur sa
   * propre ligne sous le label (à la différence du `delta` qui reste inline
   * à droite du label).
   */
  secondaryBadge?: { label: string; tone: BadgeTone };
  /** Si fourni, la valeur devient un bouton cliquable (drill-down). */
  onValueClick?: () => void;
  /**
   * Contenu rendu à droite de la ligne du badge `delta` (uniquement en
   * `deltaPosition='below'`), aligné en bout de ligne (`justify-between`).
   * Utilisé par la card CA prévisionnel pour son toggle « Brut » sans
   * ajouter de ligne supplémentaire sous la card.
   */
  deltaTrailing?: ReactNode;
  className?: string;
}

const DELTA_BADGE_STYLES: Record<DeltaDirection, string> = {
  up: 'bg-success-50 border-success-100 text-success-600',
  down: 'bg-danger-50 border-danger-100 text-danger-600',
  neutral: 'bg-gray-50 border-gray-200 text-gray-600',
  new: 'bg-gray-50 border-gray-200 text-gray-600',
};

const TONE_BADGE_STYLES: Record<BadgeTone, string> = {
  success: 'bg-success-50 border-success-100 text-success-600',
  danger: 'bg-danger-50 border-danger-100 text-danger-600',
  neutral: 'bg-gray-50 border-gray-200 text-gray-600',
};

export function StatCard({
  icon,
  label,
  value,
  hint,
  delta,
  deltaPosition = 'inline',
  secondaryBadge,
  onValueClick,
  deltaTrailing,
  className,
}: StatCardProps) {
  const renderDeltaBadge = () =>
    delta && (
      <span
        className={cn(
          'inline-flex shrink-0 items-center gap-0.5 rounded-full border px-1.5 py-0.5 text-[11px] font-semibold',
          DELTA_BADGE_STYLES[delta.direction],
        )}
      >
        {delta.direction === 'up' && <ArrowUpRight className="h-3 w-3" />}
        {delta.direction === 'down' && <ArrowDownRight className="h-3 w-3" />}
        {delta.direction === 'neutral' && <Minus className="h-3 w-3" />}
        {delta.direction === 'new' ? 'new' : delta.value}
      </span>
    );

  return (
    <div className={cn('rounded-lg bg-white p-6 shadow-sm', className)}>
      <div className="flex items-start justify-between gap-3">
        {onValueClick ? (
          <button
            type="button"
            onClick={onValueClick}
            className="text-left text-[28px] font-semibold leading-none tracking-tight text-brand transition-colors hover:text-accent focus-visible:text-accent"
          >
            {value}
          </button>
        ) : (
          <p className="text-[28px] font-semibold leading-none tracking-tight text-brand">
            {value}
          </p>
        )}
        {icon && (
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent/10 text-accent">
            {icon}
          </div>
        )}
      </div>
      {/* Label (+ delta inline à droite si deltaPosition='inline') */}
      <div className="mt-4 flex items-center justify-between gap-2">
        <p className="text-[11px] font-medium uppercase tracking-wider text-text-muted">{label}</p>
        {deltaPosition === 'inline' && renderDeltaBadge()}
      </div>
      {/* Delta sur sa propre ligne (deltaPosition='below'). Le suffixe « vs N-1 »
          est désormais implicite : il est porté par un tooltip au survol du
          badge. `deltaTrailing` (ex. toggle « Brut » de la card CA prévisionnel)
          se place en bout de ligne sans créer de ligne supplémentaire. */}
      {deltaPosition === 'below' && (delta || deltaTrailing) && (
        <div className="mt-2 flex items-center justify-between gap-2">
          {delta ? (
            <Tooltip
              content={
                delta.direction === 'new'
                  ? 'Aucune donnée sur la période précédente'
                  : 'Variation vs période précédente (N-1)'
              }
              side="top"
              align="start"
            >
              {renderDeltaBadge()}
            </Tooltip>
          ) : (
            <span />
          )}
          {deltaTrailing}
        </div>
      )}
      {/* Badge secondaire sur sa propre ligne sous le label */}
      {secondaryBadge && (
        <div className="mt-2">
          <span
            className={cn(
              'inline-flex items-center rounded-full border px-1.5 py-0.5 text-[11px] font-semibold',
              TONE_BADGE_STYLES[secondaryBadge.tone],
            )}
          >
            {secondaryBadge.label}
          </span>
        </div>
      )}
      {hint && <p className="mt-2 text-xs text-text-muted">{hint}</p>}
    </div>
  );
}
